/**
 * Webhooks Contracts
 * API contracts for webhook management (org-admin)
 */

import { z } from 'zod/v4'

// ============================================================================
// WEBHOOK TYPES
// ============================================================================

/**
 * Webhook type enum
 */
export const webhookTypeEnum = z.enum(['http', 'slack', 'discord', 'telegram'])

export type WebhookType = z.infer<typeof webhookTypeEnum>

/**
 * Webhook event types
 */
export const webhookEventTypes = z.enum([
  // Transaction events
  'transaction.imported',
  'transaction.updated',
  'transaction.deleted',
  'transaction.bulk_imported',
  // Import events
  'import.started',
  'import.completed',
  'import.failed',
  'import.validated',
  // Report events
  'report.ready',
  'report.failed',
  'report.downloaded',
  // User events
  'user.invited',
  'user.suspended',
  'user.activated',
  'user.role_changed',
  'user.removed',
  // Subscription events
  'subscription.created',
  'subscription.updated',
  'subscription.cancelled',
  'subscription.renewed',
  'subscription.expiring',
  'subscription.expired',
  // Invoice events
  'invoice.paid',
  'invoice.failed',
  'invoice.created',
  'invoice.overdue',
  // Security events
  'user.login',
  'user.failed_login',
  'user.password_changed',
  'user.password_reset_requested',
  'user.mfa_enabled',
  'user.mfa_disabled',
  'suspicious_activity',
  // Tenant events
  'tenant.created',
  'tenant.suspended',
  'tenant.activated',
  'tenant.settings_updated',
  // Balance events
  'balance.snapshot_ready',
  'balance.threshold_exceeded',
  // Alert events
  'price.alert.triggered',
])

export type WebhookEventType = z.infer<typeof webhookEventTypes>

/**
 * Webhook delivery status enum
 */
export const webhookDeliveryStatusEnum = z.enum(['success', 'failed', 'retrying', 'pending'])

export type WebhookDeliveryStatus = z.infer<typeof webhookDeliveryStatusEnum>

// ============================================================================
// REQUEST SCHEMAS
// ============================================================================

/**
 * Create webhook request schema
 */
export const createWebhookRequestSchema = z.object({
  name: z.string().min(1).max(255),
  type: webhookTypeEnum,
  url: z.string().url(),
  events: z.array(webhookEventTypes).min(1),
  config: z.record(z.string(), z.unknown()).optional(),
  isActive: z.boolean().optional(),
})

export type CreateWebhookRequest = z.infer<typeof createWebhookRequestSchema>

/**
 * Update webhook request schema
 */
export const updateWebhookRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  url: z.string().url().optional(),
  events: z.array(webhookEventTypes).min(1).optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  isActive: z.boolean().optional(),
})

export type UpdateWebhookRequest = z.infer<typeof updateWebhookRequestSchema>

/**
 * Webhook ID params schema
 */
export const webhookIdParamsSchema = z.object({
  webhookId: z.string().uuid(),
})

export type WebhookIdParams = z.infer<typeof webhookIdParamsSchema>

/** The page window every webhook list querystring takes: 1 to 100 rows from an offset. */
const offsetPaginationQueryShape = {
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
}

/**
 * Webhook deliveries query schema
 */
export const webhookDeliveriesQuerySchema = z.object({
  status: webhookDeliveryStatusEnum.optional(),
  eventType: webhookEventTypes.optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  ...offsetPaginationQueryShape,
})

export type WebhookDeliveriesQuery = z.infer<typeof webhookDeliveriesQuerySchema>

/**
 * Webhook list query schema
 */
export const webhookListQuerySchema = z.object({
  type: webhookTypeEnum.optional(),
  isActive: z.coerce.boolean().optional(),
  ...offsetPaginationQueryShape,
})

export type WebhookListQuery = z.infer<typeof webhookListQuerySchema>

// ============================================================================
// RESPONSE SCHEMAS
// ============================================================================

/**
 * Webhook response schema
 */
export const webhookResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  type: webhookTypeEnum,
  url: z.string(),
  events: z.array(webhookEventTypes),
  config: z.record(z.string(), z.unknown()),
  isActive: z.boolean(),
  lastTriggeredAt: z.string().datetime().nullable(),
  failureCount: z.number(),
  lastFailureAt: z.string().datetime().nullable(),
  lastFailureReason: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type WebhookResponse = z.infer<typeof webhookResponseSchema>

/**
 * Webhook delivery response schema
 */
export const webhookDeliveryResponseSchema = z.object({
  id: z.string().uuid(),
  webhookId: z.string().uuid(),
  eventType: webhookEventTypes,
  status: webhookDeliveryStatusEnum,
  statusCode: z.number().nullable(),
  response: z.string().nullable(),
  attemptedAt: z.string().datetime(),
  duration: z.number().nullable(),
  attemptNumber: z.number(),
  nextRetryAt: z.string().datetime().nullable(),
  errorMessage: z.string().nullable(),
  errorType: z.string().nullable(),
})

export type WebhookDeliveryResponse = z.infer<typeof webhookDeliveryResponseSchema>

/**
 * Test webhook response schema
 */
export const testWebhookResponseSchema = z.object({
  success: z.boolean(),
  statusCode: z.number().optional(),
  response: z.string().optional(),
  error: z.string().optional(),
  duration: z.number(),
  timestamp: z.string().datetime(),
})

export type TestWebhookResponse = z.infer<typeof testWebhookResponseSchema>

/**
 * Webhook stats response schema
 */
export const webhookStatsResponseSchema = z.object({
  totalWebhooks: z.number(),
  activeWebhooks: z.number(),
  totalDeliveries: z.number(),
  successfulDeliveries: z.number(),
  failedDeliveries: z.number(),
  pendingDeliveries: z.number(),
  averageDeliveryTime: z.number(),
})

export type WebhookStatsResponse = z.infer<typeof webhookStatsResponseSchema>

/**
 * Webhook list response schema
 */
export const webhookListResponseSchema = z.object({
  webhooks: z.array(webhookResponseSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
})

export type WebhookListResponse = z.infer<typeof webhookListResponseSchema>

/**
 * Webhook deliveries list response schema
 */
export const webhookDeliveriesListResponseSchema = z.object({
  deliveries: z.array(webhookDeliveryResponseSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
})

export type WebhookDeliveriesListResponse = z.infer<typeof webhookDeliveriesListResponseSchema>

// ============================================================================
// ERROR SCHEMAS
// ============================================================================

/**
 * Webhook error response schema
 */
export const webhookErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
})

export type WebhookErrorResponse = z.infer<typeof webhookErrorResponseSchema>

// ============================================================================
// EVENT CATEGORY INFO
// ============================================================================

/**
 * Event category info schema
 */
export const eventCategoryInfoSchema = z.object({
  category: z.string(),
  events: z.array(webhookEventTypes),
  description: z.string(),
})

export type EventCategoryInfo = z.infer<typeof eventCategoryInfoSchema>

/**
 * Available events response schema
 */
export const availableEventsResponseSchema = z.object({
  events: z.array(
    z.object({
      type: webhookEventTypes,
      description: z.string(),
      category: z.string(),
    }),
  ),
  categories: z.array(
    z.object({
      name: z.string(),
      description: z.string(),
    }),
  ),
})

export type AvailableEventsResponse = z.infer<typeof availableEventsResponseSchema>
